
import React from 'react';

import _ from 'underscore';
import { securityAction } from '@bingads-webui-clientcenter/pidl-service-smart';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import type { WithPermissionsProps } from '@bingads-webui-react/hoc-with-permissions';
import { PaasChallenger } from '@bingads-webui-campaign-react/payment-as-a-service-challenge';
import { PidlUtil } from '../utils/pidl-util';
import { PropTypesDefaultGenericPidl } from '../shapes';
import { AdderPidlForm } from '../pidl-forms/adder-pidl-form';
import { ScaChallenger } from './sca-challenger';
import Logger from '../utils/logger';
import type {
  AdderProps,
  AdderState,
  PaymentInstrument,
  PidlError,
  PidlEventName,
  PidlEventParameters,
  PidlInstance,
} from '../types';

type AdderInternalProps = Omit<AdderProps, 'permissions'> & WithPermissionsProps;

export class AdderNoPermissions extends React.Component<AdderInternalProps, AdderState> {
  static defaultProps: Partial<AdderInternalProps> = {
    ...PropTypesDefaultGenericPidl,
    pidlLoggerCreate: () => undefined,

    challengerTestHeader: null,
    permissions: {},
    amount: 0,

    companyCodeCurrencyCardTypeMapping: null,

    countryCodeCompanyCodeMapping: null,
  };


  pidlUtil: PidlUtil;

  constructor(props: AdderInternalProps) {
    super(props);
    this.state = {
      pidlInstance: null,
      pidlFormId: _.uniqueId('pidl-container-'),
    };
    this.pidlUtil = new PidlUtil(props);
    this.props.attachSubmitEvent(() => {
      if (this.state.pidlInstance) {
        this.pidlUtil.logger.start('submit');
        this.state.pidlInstance.moveNextPage();
      }
    });
  }

  componentDidMount() {
    const pidlCallbacks = {

      successCallback: (pi: PaymentInstrument) => this.successCallback(pi),
      goHomeCallback: () => this.goHomeCallback(),
    };

    const displayOptions = {
      containerId: this.state.pidlFormId,
      showSubmitBlock: false,
      showErrorBlock: false,
      showPlaceholderInTextbox: true,
      disableFocusOnLoad: true,
    };


    const errorHandler = (error: PidlError) => this.pidlInstanceErrorHandler(error);

    const {
      handlePidlLoadingStart = _.noop,
      handlePidlLoadingComplete = _.noop,
    } = this.props.eventHandlers;

    handlePidlLoadingStart();

    this.pidlUtil.getPidlInstance({
      piData: this.props.piData,
      pidlCallbacks,

      docDisplayEventHandler: (eventName: PidlEventName, parameters?: PidlEventParameters) => this.docDisplayEventHandler(eventName, parameters),
      displayOptions,
      errorHandler,
      securityAction: securityAction.PaymentInstrumentAdd,
    }).then((pidlInstance: PidlInstance) => {
      handlePidlLoadingComplete(); // Do not move this to finally. Must invoke the callback before setting pidlInstance to state.
      this.setState({
        pidlInstance,
      });
    }).catch((error: PidlError) => {
      handlePidlLoadingComplete();
      this.pidlInstanceErrorHandler(error);
    });
  }

  componentWillUnmount() {
    this.props.detachSubmitEvent();
  }


  pidlInstanceErrorHandler(error: PidlError) {
    this.props.eventHandlers.handleTerminatingError(error);
  }

  addOrUpdatePI = ({
    pi,
    sessionId,
  }: { pi: PaymentInstrument; sessionId?: string }) => this.pidlUtil.addOrUpdatePI({
    pi,
    sessionId,
    rurl: this.props.piData.ReturnUrl,
    piType: this.props.piData.PiType,
    addOrUpdateSuccessCallback: this.props.eventHandlers.handleAddOrUpdateSuccess,
    addOrUpdateFailureCallback: this.props.eventHandlers.handleTerminatingError,
  });


  successCallback = (pi: PaymentInstrument) => {
    const isGoogleAuthIntlNewSignInPageEnabled = _.get(this.props.permissions, ['dynamic', 'GoogleAuthIntlNewSignInPage'], false);
    const isPAAS = ['Google', 'Facebook'].includes(this.props.authenticationType);

    const piid = isPAAS ? null : pi.id;
    const walletRequestId = isPAAS ? pi.response?.requestId : null;

    this.pidlUtil.logger.setOption('piid', piid);
    this.pidlUtil.logger.setOption('walletRequestId', walletRequestId);
    this.pidlUtil.logger.succeed();

    if (this.props.piData.PiType === this.pidlUtil.piTypes.CreditCard &&
      ((!isPAAS && this.props.isPsd2ValidationEnabled) ||
      (isPAAS && isGoogleAuthIntlNewSignInPageEnabled))
    ) {
      // Set logger
      const logger = new Logger(this.props.pidlLoggerCreate('AddCard', 'ScaChallenge'));
      logger.clearOptions();
      logger.setOptions({
        country: this.props.piData.Market,
        piType: this.props.piData.PiType,
        piid,
        walletRequestId,
      });

      logger.start();
      logger.start('renderPanel');

      // Set PiData
      const piData = {
        CtpPiId: pi.id,

        MtPiId: null as number | null,
        PiType: 'CreditCard', // Pidl Enum
      };

      // Set PurchaseContext
      const purchaseContext = {
        amount: 0,
        currency: this.props.currency,
        country: this.props.piData.Market,
        challengeScenario: 'AddCard', // Pidl Enum
      };

      // Set EventHandlers
      const eventHandlers = {

        handleTerminatingError: (error: PidlError) => this.props.eventHandlers.handleScaTerminatingError?.(pi, error),
        handleProcessingStart: this.props.eventHandlers.handleScaProcessingStart,
        handlePidlDocRendered: this.props.eventHandlers.handleScaPidlDocRendered,
        handleProcessingComplete: this.props.eventHandlers.handleScaProcessingComplete,
        handleGoHome: () => this.props.eventHandlers.handleScaGoHome(pi),

        handleScaChallengeSuccess: (sessionId: string) => {
          this.props.eventHandlers.handleScaChallengeSuccess?.(sessionId);

          this.addOrUpdatePI({
            pi,
            sessionId,
          });
        },
        handleScaChallengeFailure: () => this.props.eventHandlers.handleScaChallengeFailure(pi),
      };

      if (isPAAS) {
        const {
          handlePidlLoadingStart = _.noop,
          handlePidlLoadingComplete = _.noop,
        } = this.props.eventHandlers;

        // Set shared event handlers for PAAS/SCA
        const paasEventHandlers = {
          handleDataLoading: () => {
            handlePidlLoadingStart();
          },
          handleDataLoaded: () => {
            handlePidlLoadingComplete();
          },

          handleScaChallengeSuccess: (sessionId: string, paasPiId: number) => {
            this.props.eventHandlers.handleScaChallengeSuccess?.(sessionId);
            const patchedPi = {
              ...pi,
              id: paasPiId,
            };
            this.addOrUpdatePI({ pi: patchedPi, sessionId });
          },
          handleScaChallengeFailure: eventHandlers.handleScaChallengeFailure,
          handleCancel: eventHandlers.handleGoHome,
        };

        const {
          paymentAsAServiceApi, aid, cid, pidlEnvironment,
        } = this.props;

        // Set PAAS challenge data
        this.setState({
          scaChallengerData: {
            isPaasChallenge: true,
            paymentAsAServiceApi,
            aid,
            cid,
            walletRequestId,
            i18n: this.props.i18n,
            pidlEnvironment,
            eventHandlers: paasEventHandlers,
            pidlLogger: logger,
          },
        });
      } else {
        this.setState({
          scaChallengerData: {
            piData,
            purchaseContext,
            pidlLogger: logger.pidlLogger,
            eventHandlers,
            testHeader: this.props.challengerTestHeader,
          },
        });
      }
    } else if (this.props.piData.PiType === this.pidlUtil.piTypes.UPI) {
      // need to make a HandlePaymentChallenge PIDL call right in the success callback to charge the UPI for the amount on the add funds page
      // Set logger
      const logger = new Logger(this.props.pidlLoggerCreate('AddUPI', 'ScaChallenge'));
      logger.clearOptions();
      logger.setOptions({
        country: this.props.piData.Market,
        piType: this.props.piData.PiType,
        piid: pi.id,
      });

      logger.start();
      logger.start('renderPanel');
      // None of these actually do anything in Billdesk, but these are set to avoid null exceptions in pidl-util
      const eventHandlers = {

        handleTerminatingError: (error: PidlError) => this.props.eventHandlers.handleScaTerminatingError?.(pi, error),
        handleProcessingStart: this.props.eventHandlers.handleScaProcessingStart,
        handlePidlDocRendered: this.props.eventHandlers.handleScaPidlDocRendered,
        handleProcessingComplete: this.props.eventHandlers.handleScaProcessingComplete,
        handleGoHome: () => this.props.eventHandlers.handleScaGoHome(pi),

        handleScaChallengeSuccess: (sessionId: string) => {
          this.props.eventHandlers.handleScaChallengeSuccess?.(sessionId);
        },
        handleScaChallengeFailure: () => this.props.eventHandlers.handleScaChallengeFailure(pi),
      };
      // Set PiData
      const piData = {
        IsUPIChallenge: true,
        CtpPiId: pi.id,

        MtPiId: null as number | null,
        PiType: 'UPI', // Pidl Enum
        CtpAccountId: pi.response.accountId,
        Amount: this.props.piData.Amount,
        CustomerId: this.props.piData.CustomerId,
        AccountId: this.props.piData.AccountId,
        IsPrepay: this.props.piData.IsPrepay,
        ReturnUrl: this.props.piData.ReturnUrl, // should go to success receipt page
        FailureUrl: this.props.piData.FailureUrl, // should go to original page
      };
      // Set PurchaseContext with non-zero amount
      const purchaseContext = {
        amount: this.props.amount,
        currency: this.props.currency,
        country: this.props.piData.Market,
        challengeScenario: 'PaymentTransaction', // Pidl Enum
      };
      this.setState({
        scaChallengerData: {
          piData,
          purchaseContext,
          pidlLogger: logger.pidlLogger,
          eventHandlers,
          testHeader: this.props.challengerTestHeader,
        },
      });
    } else {
      this.addOrUpdatePI({
        pi,
      });
    }
  };

  goHomeCallback = () => {
    this.props.eventHandlers.handleGoHome();
  };


  docDisplayEventHandler(eventName: PidlEventName, parameters?: PidlEventParameters | string) {
    let error;
    switch (eventName) {
      case 'pageRendering':
        this.props.eventHandlers.handleProcessingStart();
        break;
      case 'pageRendered':
        this.pidlUtil.logger.succeed('renderPidl');
        this.props.eventHandlers.handlePidlDocRendered();
        break;
      case 'error':
        error = typeof parameters === 'object' && parameters !== null ? parameters.error : undefined;
        // in case of server errors, don't handle them here
        // They will be handled when they call failure callback
        if (error && error.status !== 'error') {
          this.pidlUtil.logger.validationFail('submit', error);
        }
        this.props.eventHandlers.handleProcessingComplete();
        break;
      default:
        break;
    }
  }

  render() {
    // scaChallengerData is set in the Adder callback, so this is only
    // set if the pi has been added successfully, and needs challenged
    if (this.state.scaChallengerData) {
      const challengerProps = _.extend(
        {},
        // most of the props will be the same for the challenger
        this.props,
        // this is last so we override the piData/eventHandlers/logger from props
        this.state.scaChallengerData
      );

      if (this.state.scaChallengerData.isPaasChallenge) {
        return (
          <PaasChallenger
            {...challengerProps}
            isPidlSecureFieldsIsolationAdoptionEnabled={_.get(this.props.permissions, ['dynamic', 'PidlSecureFieldsIsolationAdoption'], false)}
          />
        );
      }

      return (

        <ScaChallenger
          {...challengerProps}
        />
      );
    }
    return (
      <div className="adder-container">
        <AdderPidlForm
          currency={this.props.currency}
          pidlInstance={this.state.pidlInstance}
          pidlUtil={this.pidlUtil}
          piData={this.props.piData}
          pidlFormId={this.state.pidlFormId}
          handleProcessingStart={this.props.eventHandlers.handleProcessingStart}
          handlePidlLoadingStart={this.props.eventHandlers.handlePidlLoadingStart}
          handlePidlLoadingComplete={this.props.eventHandlers.handlePidlLoadingComplete}
        />
      </div>
    );
  }
}


export const Adder = withPermissions<Omit<AdderProps, 'permissions'>>(AdderNoPermissions);
